import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Loader, Select, TextInput } from "@mantine/core";
import { IconArrowsSort, IconSearch } from "@tabler/icons-react";
import { Platform, Platforms, PlatformUnitType } from "./types";
import { mapPlatformStatus, statusPill, unitDisplayStatus } from "./status";
import { FilterChips, type FilterChip } from "../FilterChips";
import { PlatformStatusPill } from "./PlatformStatusPill";
import { useIsNarrow } from "../useIsNarrow";
import { loadStoredValue, storeValue } from "../storedValue";

// Inline styles (not Tailwind): these views ship inside the robotics bundle,
// which does NOT include Tailwind — only Mantine (host-provided) + inline CSS.
const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate800: "#1e293b",
  slate900: "#0f172a",
  border: "#e2e8f0",
  white: "#ffffff",
  greenText: "#059669",
  blueText: "#2563eb",
  slate50: "#f8fafc",
};

interface PlatformsViewProps {
  data?: Platforms;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  locationLabel?: string;
  onSelectPlatform?: (platform: Platform) => void;
  platformSide?: boolean;
  tValue?: boolean;
  currentWeight?: boolean;
}

const STATUS_FILTERS = ["All", "Available", "In Transit", "At Dock", "Returning", "Maintenance", "Fault"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_FILTER_STORAGE_KEY = "robotics-platforms-status-filter";

function matchesSearch(platform: Platform, unitTypeName: string | undefined, term: string) {
  if (!term) return true;
  const haystack = [
    platform.platformNumber,
    platform.currentUnitNumber ?? "",
    unitTypeName ?? "",
    ...platform.units.flatMap((u) => [u.unitNumber, u.owner?.name ?? "", u.owner?.email ?? ""]),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(term);
}

function typeNameOf(platform: Platform, unitTypeById: Map<number, PlatformUnitType>) {
  return platform.platformUnitTypeId != null
    ? unitTypeById.get(platform.platformUnitTypeId)?.typeName
    : undefined;
}

const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

function firstUnitNumber(platform: Platform): string | undefined {
  return platform.units.map((u) => u.unitNumber).sort(naturalCompare)[0];
}

const SORT_OPTIONS = [
  { value: "platform", label: "Platform" },
  { value: "unit", label: "Unit" },
] as const;
type SortBy = (typeof SORT_OPTIONS)[number]["value"];

const SORT_BY_STORAGE_KEY = "robotics-platforms-sort-by";
const SORT_BY_VALUES = SORT_OPTIONS.map((o) => o.value);

function sortPlatforms(platforms: Platform[], sortBy: SortBy): Platform[] {
  return [...platforms].sort((a, b) => {
    if (sortBy === "platform") return naturalCompare(a.platformNumber, b.platformNumber);
    const ua = firstUnitNumber(a);
    const ub = firstUnitNumber(b);
    if (!ua && !ub) return 0;
    if (!ua) return 1; // platforms with no units sort to the end
    if (!ub) return -1;
    return naturalCompare(ua, ub);
  });
}

const miniLabel: CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400 };

export function PlatformsView({
  data,
  isLoading,
  isError,
  error,
  locationLabel,
  onSelectPlatform,
  platformSide = false,
  tValue = false,
  currentWeight = false,
}: PlatformsViewProps) {
  const unitTypeById = useMemo(
    () => new Map((data?.unitTypes ?? []).map((t) => [t.id, t])),
    [data?.unitTypes]
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() =>
    loadStoredValue(STATUS_FILTER_STORAGE_KEY, STATUS_FILTERS, "All")
  );
  const [sortBy, setSortBy] = useState<SortBy>(() =>
    loadStoredValue(SORT_BY_STORAGE_KEY, SORT_BY_VALUES, "platform")
  );
  const term = search.trim().toLowerCase();
  const narrow = useIsNarrow();

  useEffect(() => storeValue(STATUS_FILTER_STORAGE_KEY, statusFilter), [statusFilter]);
  useEffect(() => storeValue(SORT_BY_STORAGE_KEY, sortBy), [sortBy]);

  const filtered = useMemo(
    () =>
      sortPlatforms(
        (data?.platforms ?? []).filter(
          (p) =>
            matchesSearch(p, typeNameOf(p, unitTypeById), term) &&
            (statusFilter === "All" || mapPlatformStatus(p.status) === statusFilter)
        ),
        sortBy
      ),
    [data?.platforms, term, statusFilter, unitTypeById, sortBy]
  );

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#991b1b", marginBottom: 8 }}>Error Loading Platforms</div>
        <div style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Something went wrong."}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: narrow ? 12 : 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400, marginBottom: 4 }}>
          Platforms — {locationLabel ?? "—"}
        </div>
        {!narrow && (
          <div style={{ color: c.slate500 }}>
            Every platform and the units mapped to it. Click a unit to request it to the dock,
            return it to storage, or reset a stuck platform.
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: narrow ? 10 : 12, marginBottom: narrow ? 16 : 24 }}>
        <div style={narrow ? { width: "100%" } : { flex: "1 1 220px", maxWidth: 360 }}>
          <TextInput
            placeholder={narrow ? "Search platform or unit…" : "Search platform or unit code…"}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
          />
        </div>
        <div style={narrow ? { flex: "1 1 0", minWidth: 0 } : undefined}>
          <FilterChips
            chips={buildStatusChips(data.platforms)}
            active={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            compact={narrow}
          />
        </div>
        <div style={{ marginLeft: "auto" }}>
          <Select
            value={sortBy}
            onChange={(v) => setSortBy((v as SortBy) ?? "platform")}
            data={SORT_OPTIONS.map(({ value, label }) => ({ value, label }))}
            allowDeselect={false}
            w={narrow ? 130 : 160}
            leftSection={<IconArrowsSort size={16} />}
            aria-label="Sort by"
          />
        </div>
      </div>

      {data.platforms.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          No platforms configured for this location.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          {search.trim()
            ? `No platforms match “${search.trim()}”${statusFilter !== "All" ? ` in “${statusFilter}”` : ""}.`
            : `No platforms are currently “${statusFilter}”.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filtered.map((platform) => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              unitTypeName={typeNameOf(platform, unitTypeById)}
              onSelectPlatform={onSelectPlatform}
              platformSide={platformSide}
              tValue={tValue}
              currentWeight={currentWeight}
              narrow={narrow}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ALWAYS_SHOWN: StatusFilter[] = ["All", "Available", "In Transit", "At Dock", "Returning"];

function buildStatusChips(platforms: Platform[]): FilterChip[] {
  const counts: Record<string, number> = {};
  for (const p of platforms) {
    const label = mapPlatformStatus(p.status);
    counts[label] = (counts[label] ?? 0) + 1;
  }
  return STATUS_FILTERS.filter(
    (f) => ALWAYS_SHOWN.includes(f) || (counts[f] ?? 0) > 0
  ).map((f) => ({
    value: f,
    label: f,
    count: f === "All" ? platforms.length : counts[f] ?? 0,
    colors: f === "All" ? undefined : statusPill(f),
  }));
}

type UnitColumns = { platformSide: boolean; tValue: boolean; currentWeight: boolean; narrow: boolean };

const unitGrid = ({ platformSide, tValue, currentWeight, narrow }: UnitColumns) =>
  narrow
    ? "minmax(0, 1fr) minmax(0, 1.4fr)"
    : `${platformSide ? "60px " : ""}${tValue ? "70px " : ""}minmax(110px, 1.2fr) ${
        currentWeight ? "110px " : ""
      }minmax(160px, 1.8fr)`;

function PlatformCard({
  platform,
  unitTypeName,
  onSelectPlatform,
  platformSide = false,
  tValue = false,
  currentWeight = false,
  narrow = false,
}: {
  platform: Platform;
  unitTypeName?: string;
  onSelectPlatform?: (platform: Platform) => void;
  platformSide?: boolean;
  tValue?: boolean;
  currentWeight?: boolean;
  narrow?: boolean;
}) {
  const UNIT_GRID = unitGrid({ platformSide, tValue, currentWeight, narrow });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: narrow ? "column" : "row",
        alignItems: "stretch",
        borderRadius: 12,
        border: `1px solid ${c.border}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        background: c.white,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => onSelectPlatform?.(platform)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
        }}
        style={{
          display: "flex",
          flexShrink: 0,
          background: c.slate50,
          cursor: onSelectPlatform ? "pointer" : "default",
          ...(narrow
            ? {
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 16px",
                borderBottom: `1px solid ${c.border}`,
              }
            : {
                width: 120,
                flexDirection: "column",
                justifyContent: "center",
                padding: 16,
                borderRight: `1px solid ${c.border}`,
              }),
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={miniLabel}>Platform</div>
          <div style={{ fontSize: narrow ? 22 : 28, fontWeight: 800, lineHeight: 1.1, color: c.slate900 }}>
            {platform.platformNumber}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              fontWeight: 600,
              color: unitTypeName ? c.slate500 : c.slate400,
              fontStyle: unitTypeName ? undefined : "italic",
              overflowWrap: "anywhere",
            }}
          >
            {unitTypeName ?? "No type"}
            {platform.platformType && (
              <span style={{ color: c.slate400 }}> - {platform.platformType}</span>
            )}
          </div>
        </div>
        {narrow && <PlatformStatusPill platform={platform} />}
      </div>

      {/* Middle: the units table */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: UNIT_GRID,
            gap: 12,
            padding: "8px 16px",
            borderBottom: `1px solid ${c.border}`,
          }}
        >
          {platformSide && !narrow && <div style={miniLabel}>Side</div>}
          {tValue && !narrow && <div style={miniLabel}>T value</div>}
          <div style={miniLabel}>Mapped Units ({platform.units.length})</div>
          {currentWeight && !narrow && <div style={miniLabel}>Current weight</div>}
          <div style={miniLabel}>Owner</div>
        </div>

        {platform.units.length === 0 ? (
          <div
            onClick={() => onSelectPlatform?.(platform)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
            }}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              color: c.slate400,
              fontSize: 13,
              padding: "12px 16px",
              cursor: onSelectPlatform ? "pointer" : "default",
            }}
          >
            No units assigned yet.
          </div>
        ) : (
          platform.units.map((unit, i) => {
            const isCurrent = platform.currentUnitId === unit.unitId;
            return (
              <div
                key={unit.unitId}
                onClick={() => onSelectPlatform?.(platform)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: UNIT_GRID,
                  gap: 12,
                  alignItems: "center",
                  padding: "10px 16px",
                  borderBottom: i < platform.units.length - 1 ? `1px solid ${c.border}` : undefined,
                  background: isCurrent ? statusPill(unitDisplayStatus(platform, unit)).bg : c.white,
                  cursor: onSelectPlatform ? "pointer" : "default",
                }}
              >
                {platformSide && !narrow && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.slate900 }}>
                    {unit.platformSide ?? "—"}
                  </div>
                )}
                {tValue && !narrow && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: unit.tValue ? c.slate900 : c.slate400 }}>
                    {unit.tValue ?? "—"}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: c.slate900 }}>Unit {unit.unitNumber}</div>
                  {narrow && (
                    <div style={{ fontSize: 12, color: c.slate500 }}>
                      {[
                        platformSide && unit.platformSide ? `Side ${unit.platformSide}` : null,
                        tValue && unit.tValue ? `T ${unit.tValue}` : null,
                        currentWeight && unit.currentWeight != null ? `${unit.currentWeight} kg` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
                {currentWeight && !narrow && (
                  <div style={{ fontSize: 13, color: c.slate500 }}>
                    {unit.currentWeight != null ? `${unit.currentWeight} kg` : "—"}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  {unit.owner ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.slate900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {unit.owner.name || "—"}
                      </div>
                      {unit.owner.email && (
                        <div style={{ fontSize: 12, color: c.slate400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {unit.owner.email}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: c.slate400, fontStyle: "italic" }}>Not rented</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!narrow && (
        <div style={{ width: 150, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px", borderLeft: `1px solid ${c.border}` }}>
          <PlatformStatusPill platform={platform} />
        </div>
      )}
    </div>
  );
}
